import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetTagColor } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import {
  Flex,
  FlexColumn,
  Chip as ChipBase,
  FlexCenter,
  FlexSpace,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import useCriticalAssets from '@elementary/common-ui/hooks/useCriticalAssets';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { Link, Tooltip } from '@mui/material';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  TREE_NODE,
  TREE_TYPE,
  TREE_VIEW_BY,
} from '@elementary/common-ui/constants/urlParams';
import useNavigateWithEnv from 'shared/hooks/useNavigateWithEnv';
import { AssetData, TestData } from 'shared/hooks/useAssetData/types';
import { fontWeight, radius, space } from '@elementary/common-ui/style/utils';
import AssetHeaderActions from './AssetHeaderActions';

const StyledLink = styled(Link)`
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.palette.content.primary};

  &:hover {
    text-decoration: underline;
  }
`;

const Chip = styled(ChipBase)`
  border: 1px solid ${({ theme }) => theme.palette.border.light};
  font-weight: ${fontWeight('bold')};
  border-radius: ${radius('capsule')};
  background: ${({ theme }) => theme.palette.surface.muted};
  color: ${({ theme }) => theme.palette.content.secondary};
  padding: ${space(0, 'sm')};
  min-width: auto;
  flex-shrink: 0;
`;

const CriticalAssetMark = ({ show }: { show: boolean }) => (
  <Tooltip title="Critical asset">
    <FlexCenter>{show && <CriticalAssetTagColor />}</FlexCenter>
  </Tooltip>
);

const getChipLabel = (asset?: AssetData, testData?: TestData): string => {
  if (testData) return 'test';

  const artifactType = asset?.artifactType ?? null;
  const materialization = asset?.materialization ?? null;

  if (artifactType && ['source', 'seed', 'snapshot'].includes(artifactType)) {
    return artifactType;
  }

  if (materialization && ['view', 'table'].includes(materialization)) {
    return materialization;
  }

  if (artifactType === 'model') {
    return 'table';
  }

  if (artifactType === 'semantic_view') {
    if (asset?.parentId) {
      return 'Element';
    }
    return asset?.semanticType || 'semantic view';
  }

  if (artifactType === 'exposure') {
    return asset?.biType || asset?.type || 'unknown';
  }

  if (artifactType === 'connector') {
    return 'connector';
  }

  return 'unknown';
};

type Props = {
  asset?: AssetData;
  testData?: TestData;
  clickableTitle?: boolean;
  showActions?: boolean;
};

const AssetHeader = ({
  asset,
  testData,
  clickableTitle = false,
  showActions = false,
}: Props) => {
  const navigate = useNavigateWithEnv();
  const [searchParams] = useSearchParams();
  const { formatUrlParam } = useSelectedTreeItem();
  const [internalIsCriticalAssets, setInternalIsCriticalAssets] =
    useState(false);

  const testName = testData?.name;
  const assetId = asset?.id || '';
  const isCriticalAssetsIds = assetId && !testData ? [assetId] : [];
  const { criticalAssetsData } = useCriticalAssets(
    isCriticalAssetsIds ? { ids: isCriticalAssetsIds } : {},
  );
  const { data } = criticalAssetsData;
  const isCritical = data?.[0]?.isCritical || false;

  useEffect(() => {
    setInternalIsCriticalAssets(isCritical);
  }, [isCritical]);

  const title = asset?.name || testName || 'No asset selected';
  const chipLabel = getChipLabel(asset, testData);

  const handleTitleClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    const urlParam = formatUrlParam(assetId);
    if (!urlParam) return;

    const newParams = new URLSearchParams();
    const treeType = searchParams.get(TREE_TYPE);
    const treeViewBy = searchParams.get(TREE_VIEW_BY);
    if (treeType) newParams.set(TREE_TYPE, treeType);
    if (treeViewBy) newParams.set(TREE_VIEW_BY, treeViewBy);
    newParams.set(TREE_NODE, urlParam);

    navigate(`/catalog?${newParams.toString()}`);
    e?.stopPropagation();
  };

  return (
    <FlexColumn sx={{ gap: 2 }}>
      <FlexSpace sx={{ alignItems: 'center', gap: 2 }}>
        <Flex sx={{ alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <OverflowTooltip bold popperSx={{ a: { color: 'content.inverse' } }}>
            {clickableTitle ? (
              <StyledLink onClick={handleTitleClick}>{title}</StyledLink>
            ) : (
              title
            )}
          </OverflowTooltip>

          {!testData && <CriticalAssetMark show={!!internalIsCriticalAssets} />}

          <Chip label={chipLabel.toUpperCase()} color="default" size="small" />
        </Flex>

        {showActions && (
          <AssetHeaderActions asset={asset} testData={testData} />
        )}
      </FlexSpace>
    </FlexColumn>
  );
};

export default AssetHeader;
